/* eslint-disable camelcase */
/* eslint-disable no-await-in-loop */
import { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { GreenroomState } from '../stores/types/types';
import useShotMemos from './use-shot-memos';
import { ROOM_STATES } from '../stores/reducers/settings';
import type { StreamMedia } from '../stores/types/settings';
import type { MediaItem } from '../stores/types/media';
import type { GraphicInterface } from '../stores/types/graphics';
import { mediaDeckHasVideo } from '../utils/utils';
import { DOC_TYPES } from '../types/constants';
import { RECORDING_OPTIONS } from '../components/broadcasting/constants';

const useMedia = () => {
	const featureFlags = useSelector((state: GreenroomState) => state.SettingsReducer.featureFlags);
	const roomState = useSelector((state: GreenroomState) => state.MeetingReducer.roomState);
	const user = useSelector((state: GreenroomState) => state.SessionReducer.user);
	const host = useSelector((state: GreenroomState) => state.SessionReducer.host);
	const selectedRecordingOption = useSelector((s: GreenroomState) => s.BroadcastReducer.selectedRecordingOption);

	const { currentShot } = useShotMemos();

	const initStreamMedia = {
		content_url: '',
		thumb_url: '',
		type: '',
		name: '',
		loop: false,
		timer: 0,
		background: '#000000',
		is_video: false,
		scale: 100,
		audio: '',
		countdown: 0,
		video_duration: 0,
	};
	const [allowAudioUpload, setAllowAudioUpload] = useState<boolean>(false);
	const [mediaInModal, setMediaInModal] = useState<StreamMedia>(initStreamMedia);
	const [progress, setProgress] = useState(0);
	const [progressState, setProgressState] = useState('');
	const [uploadingStreamMedia, setUploadingStreamMedia] = useState<boolean>(false);
	const preInputRef = useRef<HTMLInputElement | null>(null);
	const postInputRef = useRef<HTMLInputElement | null>(null);
	const totalBytesToUpload = useRef(0);
	const totalUploadedBytes = useRef(0);
	const lastUploadedBytes = useRef(0);

	// eslint-disable-next-line
	const saveStreamMedia = (streamMedia: StreamMedia, preOrPost: string) => {
		setUploadingStreamMedia(false);
		setMediaInModal(streamMedia);
		setAllowAudioUpload(!streamMedia.is_video);
	};

	const canEditOrControlMedia = (
		mediaItem: MediaItem | GraphicInterface,
		userObject: any,
		isHost: boolean,
		controlsOnly: boolean,
		roomStateObject?: number,
	) => {
		if (process.env.SHOW_MEDIA_CONTROLS) return true;

		const inCurrentShotControls = controlsOnly && currentShot && user
			? currentShot.shot_data.shotFeeds.some(
				(curFeed) => curFeed.presenter === userObject.gr_user,
			) : false;

		const isControlsForShowbuilder = controlsOnly && roomStateObject === ROOM_STATES.EDITING;

		return isHost
			|| (userObject && mediaItem?.created_by === userObject.gr_user)
			// @ts-ignore
			|| (userObject && mediaItem?.shared_with?.split(',')?.includes(userObject.gr_user.toString()))
			|| inCurrentShotControls
			|| isControlsForShowbuilder;
	};

	const canDeleteMedia = (
		mediaItem: MediaItem | GraphicInterface,
		userObject: any,
		isHost: boolean,
	) => isHost || (userObject && mediaItem?.created_by === userObject.gr_user);

	const canTrimMediaItem = useCallback((mediaItem: MediaItem | null | undefined): boolean => {
		if (!mediaItem) {
			return false;
		}

		return (
			(roomState === ROOM_STATES.EDITING || (roomState === ROOM_STATES.SHOWTIME && selectedRecordingOption === RECORDING_OPTIONS.RECORD))
			&& canEditOrControlMedia(mediaItem, user, host, false)
			&& mediaItem?.media_data?.image_array
			&& mediaDeckHasVideo(mediaItem.media_data.image_array)
			&& (mediaItem.num_pages === 1 || !mediaItem.num_pages)
		);
	}, [featureFlags, roomState, user, host, selectedRecordingOption]);

	const canDownloadMedia = (mediaItem: MediaItem) => !DOC_TYPES.includes(mediaItem.type);

	return {
		totalBytesToUpload,
		totalUploadedBytes,
		lastUploadedBytes,
		preInputRef,
		postInputRef,
		uploadingStreamMedia,
		setUploadingStreamMedia,
		progress,
		progressState,
		setProgress,
		setProgressState,
		mediaInModal,
		setMediaInModal,
		initStreamMedia,
		allowAudioUpload,
		setAllowAudioUpload,
		saveStreamMedia,
		canEditOrControlMedia,
		canDeleteMedia,
		canTrimMediaItem,
		canDownloadMedia,
	};
};

export default useMedia;
